import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        toast.error('Payment verification timed out. Please check your email.');
        return;
      }
      
      try {
        const response = await api.get(`/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment successful! Your membership is now active.');
          return;
        }
        
        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus('error');
          toast.error('Failed to verify payment');
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-heading font-bold mb-2" data-testid="payment-checking-status">Verifying Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2" data-testid="payment-success-status">Payment Successful!</h2>
            <p className="text-muted-foreground mb-6">Your membership is now active. Welcome to Wholesome Fitness!</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-primary hover:bg-primary/90"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <h2 className="text-2xl font-heading font-bold mb-2 text-destructive">Verification Failed</h2>
            <p className="text-muted-foreground mb-6">We couldn't verify your payment. Please contact support.</p>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
            >
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
